import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Coins, TrendingUp, Trophy, Shield, ArrowRight, Sparkles, Zap, BarChart3, Users, Ticket, Dices, Gamepad2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import LanguageToggle from '../components/LanguageToggle';

// 3D tilt card that follows the cursor
const TiltCategoryCard = ({ cat, index, user, navigate }) => {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const springCfg = { stiffness: 220, damping: 18, mass: 0.5 };
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), springCfg);
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), springCfg);
    const glareX = useTransform(mx, [-0.5, 0.5], ['0%', '100%']);
    const glareY = useTransform(my, [-0.5, 0.5], ['0%', '100%']);

    const handleMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const handleLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.12, duration: 0.6 }}
            style={{ rotateX, rotateY, transformStyle: 'preserve-3d', transformPerspective: 1000 }}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            onClick={() => navigate(user ? '/lobby' : '/login')}
            className={`relative overflow-hidden rounded-2xl border-2 ${cat.border} ${cat.shadow} bg-[#0a0a0a] p-7 cursor-pointer group will-change-transform`}
            data-testid={`category-card-${cat.key}`}
        >
            {/* Gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-60 pointer-events-none`} />

            {/* Cursor-tracked glare */}
            <motion.div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    background: useTransform(
                        [glareX, glareY],
                        ([x, y]) => `radial-gradient(circle at ${x} ${y}, ${cat.accent}33 0%, transparent 50%)`
                    ),
                }}
            />

            {/* Animated icon halo */}
            <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 3, repeat: Infinity, delay: index * 0.4 }}
                className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${cat.accent}33 0%, transparent 70%)` }}
            />

            <div className="relative z-10" style={{ transform: 'translateZ(40px)' }}>
                <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                    style={{
                        backgroundColor: `${cat.accent}1A`,
                        color: cat.accent,
                        boxShadow: `0 0 24px ${cat.accent}55, inset 0 0 16px ${cat.accent}22`,
                    }}
                >
                    {cat.icon}
                </div>

                <div className="mb-2 text-xs font-bold tracking-widest uppercase" style={{ color: cat.accent }}>
                    {cat.tagline}
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">
                    {cat.title}
                </h3>
                <p className="text-[#A0AAB2] text-sm mb-5 leading-relaxed">
                    {cat.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                    {cat.badges.map((b) => (
                        <span
                            key={b}
                            className="text-[11px] font-bold px-2.5 py-1 rounded-full border"
                            style={{
                                color: cat.accent,
                                borderColor: `${cat.accent}55`,
                                backgroundColor: `${cat.accent}10`,
                            }}
                        >
                            {b}
                        </span>
                    ))}
                </div>

                <div
                    className="inline-flex items-center gap-2 text-sm font-bold group-hover:gap-3 transition-all"
                    style={{ color: cat.accent }}
                >
                    {user ? 'Play Now' : 'Get Started'} <ArrowRight className="w-4 h-4" />
                </div>
            </div>
        </motion.div>
    );
};

const Landing = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const categories = [
        {
            key: 'sportsbook',
            title: t('landing.categories.sportsbook_title'),
            tagline: t('landing.categories.sportsbook_tagline'),
            description: t('landing.categories.sportsbook_desc'),
            icon: <Gamepad2 className="w-10 h-10" />,
            accent: '#39FF14',
            gradient: 'from-[#39FF14]/20 via-[#39FF14]/5 to-transparent',
            border: 'border-[#39FF14]/40',
            shadow: 'shadow-[0_0_40px_rgba(57,255,20,0.25)]',
            badges: ['NFL', 'NBA', 'MLB', 'UFC'],
        },
        {
            key: 'lottery',
            title: t('landing.categories.lottery_title'),
            tagline: t('landing.categories.lottery_tagline'),
            description: t('landing.categories.lottery_desc'),
            icon: <Ticket className="w-10 h-10" />,
            accent: '#FFD700',
            gradient: 'from-[#FFD700]/20 via-[#FFD700]/5 to-transparent',
            border: 'border-[#FFD700]/40',
            shadow: 'shadow-[0_0_40px_rgba(255,215,0,0.25)]',
            badges: ['Pick 3', 'Pick 4', 'Cash 5', 'Powerball'],
        },
        {
            key: 'slots',
            title: t('landing.categories.slots_title'),
            tagline: t('landing.categories.slots_tagline'),
            description: t('landing.categories.slots_desc'),
            icon: <Dices className="w-10 h-10" />,
            accent: '#FF007F',
            gradient: 'from-[#FF007F]/20 via-[#FF007F]/5 to-transparent',
            border: 'border-[#FF007F]/40',
            shadow: 'shadow-[0_0_40px_rgba(255,0,127,0.25)]',
            badges: ['Daily Jackpot', 'Cashback', 'Live Lobby'],
        },
    ];

    const sports = [
        { name: "NFL", icon: "football-ball", color: "#39FF14" },
        { name: "NBA", icon: "basketball-ball", color: "#FF007F" },
        { name: "MLB", icon: "baseball-ball", color: "#FFD700" },
        { name: "Soccer", icon: "futbol", color: "#00FFFF" },
        { name: "NHL", icon: "hockey-puck", color: "#A855F7" },
        { name: "MMA/UFC", icon: "fist-raised", color: "#FF3B30" },
    ];

    return (
        <div className="min-h-screen">
            {/* Floating language toggle — always visible top-right so Spanish-speaking
                visitors find it instantly, before they even scroll. */}
            <div className="fixed top-4 right-4 z-50">
                <LanguageToggle />
            </div>

            {/* Hero Section */}
            <section className="hero-bg min-h-screen flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />
                
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <Zap className="w-6 h-6 text-[#39FF14]" />
                            <span className="text-[#39FF14] font-semibold tracking-wider uppercase text-sm">
                                {t('landing.tagline')}
                            </span>
                            <Zap className="w-6 h-6 text-[#39FF14]" />
                        </div>
                        
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6 leading-tight">
                            <span className="text-white">{t('landing.welcome_to')}</span>
                            <br />
                            <span className="text-[#FFD700]" style={{ textShadow: '0 0 40px rgba(255, 215, 0, 0.5)' }}>
                                BETohalics
                            </span>
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-[#A0AAB2] max-w-2xl mx-auto mb-10">
                            {t('landing.subtitle')}
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {user ? (
                                <Button
                                    data-testid="play-now-btn"
                                    onClick={() => navigate('/lobby')}
                                    className="btn-primary px-8 py-6 text-lg rounded-full"
                                >
                                    {t('landing.enter_lobby')} <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            ) : (
                                <Button
                                    data-testid="login-btn"
                                    onClick={() => navigate('/login')}
                                    className="btn-primary px-8 py-6 text-lg rounded-full"
                                >
                                    {t('landing.login')} <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            )}
                        </div>
                    </motion.div>
                </div>
                
                {/* Animated particles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(20)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute w-2 h-2 rounded-full bg-[#39FF14]/30"
                            initial={{ 
                                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
                                y: typeof window !== 'undefined' ? window.innerHeight : 800 
                            }}
                            animate={{ y: -20 }}
                            transition={{ 
                                duration: 5 + Math.random() * 5,
                                repeat: Infinity,
                                delay: Math.random() * 5
                            }}
                        />
                    ))}
                </div>
            </section>

            {/* Sports Showcase */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#050505]">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
                        {sports.map((sport, i) => (
                            <motion.div
                                key={sport.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                className="flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 bg-white/5"
                            >
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sport.color, boxShadow: `0 0 8px ${sport.color}` }} />
                                <span className="text-white font-bold text-sm">{sport.name}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#050505]">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-14"
                    >
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                            {t('landing.three_ways')} <span className="text-[#FFD700]">{t('landing.three_ways_accent')}</span>
                        </h2>
                        <p className="text-[#A0AAB2] max-w-xl mx-auto">
                            {t('landing.one_account')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {categories.map((cat, index) => (
                            <TiltCategoryCard
                                key={cat.key}
                                cat={cat}
                                index={index}
                                user={user}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]">
                <div className="max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                            {t('landing.how_it_works_title')} <span className="text-[#39FF14]">{t('landing.how_it_works_accent')}</span>
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { step: "01", title: t('landing.steps.01_title'), desc: t('landing.steps.01_desc') },
                            { step: "02", title: t('landing.steps.02_title'), desc: t('landing.steps.02_desc') },
                            { step: "03", title: t('landing.steps.03_title'), desc: t('landing.steps.03_desc') },
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15 }}
                                className="text-center"
                            >
                                <div className="text-5xl font-black text-[#39FF14]/20 mb-3">{item.step}</div>
                                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                <p className="text-[#A0AAB2] text-sm">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#050505]">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="card-dark p-12 relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#39FF14]/5 via-transparent to-[#FFD700]/5" />
                        <div className="relative z-10">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                                {t('landing.cta_title')} <span className="text-[#39FF14]">{t('landing.cta_accent')}</span>?
                            </h2>
                            <p className="text-[#A0AAB2] mb-8 max-w-lg mx-auto">
                                {t('landing.cta_desc')}
                            </p>
                            {!user && (
                                <Button
                                    data-testid="cta-login-btn"
                                    onClick={() => navigate('/login')}
                                    className="btn-primary px-10 py-6 text-lg rounded-full"
                                >
                                    {t('landing.login')} <Sparkles className="ml-2 w-5 h-5" />
                                </Button>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/10 bg-[#050505]">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-[#A0AAB2] text-sm">
                        {t('landing.footer')}
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
